import { Controller } from "@hotwired/stimulus"

// A line grid: add a row, remove a row, and (where the form has a totals
// panel) live VAT and totals. All sums in integer fils; VAT is display-only
// here, because the server always recomputes it (Vatable).
//
// The totals targets are OPTIONAL. Several grids have no totals panel at all —
// a goods receipt, a material issue, a requisition — and they used to satisfy
// `recalculate` by hiding three dummy spans on the page. A grid that does not
// add anything up should not have to pretend it does.
export default class extends Controller {
  static targets = ["lines", "template", "subtotal", "vat", "total"]

  VAT_RATE = 0.05

  connect() {
    this.recalculate()
  }

  addLine(event) {
    event.preventDefault()
    const html = this.templateTarget.innerHTML.replaceAll("NEW_INDEX", Date.now().toString())
    this.linesTarget.insertAdjacentHTML("beforeend", html)
    this.recalculate()
  }

  removeLine(event) {
    event.preventDefault()
    const rows = this.linesTarget.querySelectorAll("[data-invoice-form-line]")
    if (rows.length <= 1) return
    event.target.closest("[data-invoice-form-line]").remove()
    this.recalculate()
  }

  recalculate() {
    if (!this.hasSubtotalTarget) return

    let subtotal = 0
    let vat = 0

    this.linesTarget.querySelectorAll("[data-invoice-form-line]").forEach((row) => {
      // A blank quantity box means ONE — the server reads it that way (the box
      // shows "1" as a placeholder), so the live totals must too.
      const quantityText = (row.querySelector("[data-quantity]")?.value ?? "").replaceAll(",", "").trim()
      const quantity = quantityText === "" ? 1 : (parseFloat(quantityText) || 0)
      const priceFils = Math.round((parseFloat(row.querySelector("[data-unit-price]")?.value.replaceAll(",", "")) || 0) * 100)
      const discountFils = Math.round((parseFloat(row.querySelector("[data-discount]")?.value.replaceAll(",", "")) || 0) * 100)
      const treatment = row.querySelector("[data-vat-treatment]")?.value

      // A discount reduces the TAXABLE value — VAT follows the net figure.
      const lineSubtotal = Math.max(Math.round(quantity * priceFils) - discountFils, 0)
      // Reverse-charge VAT is self-assessed (shown for reference) but never
      // owed to the supplier, so it stays out of every total.
      const vatBearing = treatment === "standard" || treatment === "reverse_charge"
      const lineVat = vatBearing ? Math.round(lineSubtotal * this.VAT_RATE) : 0
      const payableVat = treatment === "standard" ? lineVat : 0

      const vatCell = row.querySelector("[data-line-vat]")
      const totalCell = row.querySelector("[data-line-total]")
      if (vatCell) vatCell.textContent = this.format(lineVat)
      if (totalCell) totalCell.textContent = this.format(lineSubtotal + payableVat)

      subtotal += lineSubtotal
      vat += payableVat
    })

    this.subtotalTarget.textContent = this.format(subtotal)
    this.vatTarget.textContent = this.format(vat)
    this.totalTarget.textContent = this.format(subtotal + vat)
  }

  format(fils) {
    return (fils / 100).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
}
